import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { emailTemplateApi, EmailTemplate } from '../services/emailTemplateApi';

type TemplateType = 'documents' | 'rejected-documents' | 'leads';

const EmailTemplatesContent: React.FC = () => {
  const [templateType, setTemplateType] = useState<TemplateType>('documents');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    subjectTemplate: '',
    bodyTemplate: '',
    isDefault: false,
    emailSendBehavior: 'auto' as 'auto' | 'ask' | 'never'
  });

  // Track which field was last focused
  const [activeField, setActiveField] = useState<'subject' | 'body' | null>(null);
  // Track cursor positions for each field
  const [cursorPosition, setCursorPosition] = useState({
    subject: 0,
    body: 0
  });

  useEffect(() => {
    fetchTemplates();
  }, [templateType]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      let response;
      if (templateType === 'rejected-documents') {
        response = await emailTemplateApi.getRejectedDocumentsTemplates();
      } else if (templateType === 'leads') {
        response = await emailTemplateApi.getLeadsTemplates();
      } else {
        response = await emailTemplateApi.getDocumentsTemplates();
      }
      if (response.success) {
        setTemplates(response.data);
      }
    } catch (err: any) {
      setError('Failed to load templates');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      let response;
      if (templateType === 'leads') {
        response = await emailTemplateApi.createLeadsTemplate(formData);
      } else {
        response = await emailTemplateApi.createTemplate(formData, templateType);
      }
      if (response.success) {
        setShowCreateModal(false);
        resetForm();
        fetchTemplates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create template');
    }
  };

  const handleUpdate = async () => {
    if (!editingTemplate) return;
    
    try {
      const response = await emailTemplateApi.updateTemplate(editingTemplate.id, formData);
      if (response.success) {
        setEditingTemplate(null);
        resetForm();
        fetchTemplates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update template');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await emailTemplateApi.deleteTemplate(id);
      fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete template');
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await emailTemplateApi.setDefaultTemplate(id);
      fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to set default template');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subjectTemplate: '',
      bodyTemplate: '',
      isDefault: false,
      emailSendBehavior: 'auto'
    });
    setActiveField(null);
    setCursorPosition({
      subject: 0,
      body: 0
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (template: EmailTemplate) => {
    setFormData({
      title: template.title,
      subjectTemplate: template.subjectTemplate,
      bodyTemplate: template.bodyTemplate,
      isDefault: template.isDefault,
      emailSendBehavior: (template as any).emailSendBehavior || 'auto'
    });
    setEditingTemplate(template);
  };

  const insertTag = (tag: string) => {
    // Use the last active field, or default to 'body'
    const field = activeField || 'body';
    const currentValue = field === 'subject' ? formData.subjectTemplate : formData.bodyTemplate;
    const cursorPos = cursorPosition[field];
    
    // Insert the tag at the cursor position
    const beforeCursor = currentValue.substring(0, cursorPos);
    const afterCursor = currentValue.substring(cursorPos);
    const newValue = beforeCursor + `{{${tag}}}` + afterCursor;
    
    setFormData(prev => ({
      ...prev,
      [field === 'subject' ? 'subjectTemplate' : 'bodyTemplate']: newValue
    }));

    // Update cursor position to be after the inserted tag
    const newCursorPos = cursorPos + tag.length + 2; // +2 for {{}}
    setCursorPosition(prev => ({
      ...prev,
      [field]: newCursorPos
    }));

    // Set focus back to the appropriate input after a brief delay to allow state update
    setTimeout(() => {
      const input = field === 'subject' 
        ? document.getElementById('subject-input') as HTMLInputElement
        : document.getElementById('body-input') as HTMLTextAreaElement;
      if (input) {
        input.focus();
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 10);
  };

  const handleInputFocus = (field: 'subject' | 'body') => {
    setActiveField(field);
  };

  const handleInputChange = (field: 'subject' | 'body', value: string, selectionStart: number | null) => {
    if (field === 'subject') {
      setFormData(prev => ({ ...prev, subjectTemplate: value }));
    } else {
      setFormData(prev => ({ ...prev, bodyTemplate: value }));
    }
    
    // Update cursor position if available
    if (selectionStart !== null) {
      setCursorPosition(prev => ({
        ...prev,
        [field]: selectionStart
      }));
    }
  };

  const getAvailableTags = () => {
    if (templateType === 'leads') {
      return [
        { key: 'lead_name', label: 'Lead Name' },
        { key: 'lead_first_name', label: 'First Name' },
        { key: 'lead_last_name', label: 'Last Name' },
        { key: 'lead_email', label: 'Email' },
        { key: 'lead_id', label: 'Lead ID' },
        { key: 'lead_date', label: 'Lead Date' },
        { key: 'lead_company', label: 'Company' },
        { key: 'lead_phone', label: 'Phone' },
        { key: 'lead_mobile', label: 'Mobile' },
        { key: 'lead_job_title', label: 'Job Title' },
        { key: 'current_date', label: 'Current Date' }
      ];
    } else {
      return [
        { key: 'lead_name', label: 'Lead Name' },
        { key: 'lead_email', label: 'Lead Email' },
        { key: 'document_type', label: 'Document Type' },
        { key: 'document_upload_link', label: 'Upload Link' }
      ];
    }
  };

  const availableTags = getAvailableTags();

  return (
    <div className="bg-white rounded-lg shadow border">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Email Templates</h3>
            <p className="mt-1 text-sm text-gray-600">
              {templateType === 'leads' 
                ? 'Manage email templates for lead welcome emails'
                : 'Manage email templates for document requests'}
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Create Template
          </button>
        </div>

        {/* Template Type Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setTemplateType('documents')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                templateType === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Document Requests
            </button>
            <button
              onClick={() => setTemplateType('rejected-documents')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                templateType === 'rejected-documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Rejected Document Re-requests
            </button>
            <button
              onClick={() => setTemplateType('leads')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                templateType === 'leads'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Lead Welcome Emails
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-600">No email templates found</p>
            <button
              onClick={openCreateModal}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Create your first template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg shadow border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {template.title}
                      </h3>
                      {template.isDefault && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <StarIcon className="w-3 h-3 mr-1" />
                          Default
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                      <span className="font-medium">Subject:</span> {template.subjectTemplate}
                    </p>
                    <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                      <span className="font-medium">Body:</span> {template.bodyTemplate.substring(0, 100)}...
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {!template.isDefault && (
                      <button
                        onClick={() => handleSetDefault(template.id)}
                        className="p-2 text-gray-400 hover:text-yellow-600"
                        title="Set as default"
                      >
                        <StarIcon className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => openEditModal(template)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Edit template"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Delete template"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingTemplate ? 'Edit Template' : `Create New ${templateType === 'rejected-documents' ? 'Rejected Document' : templateType === 'leads' ? 'Lead Welcome' : 'Document'} Template`}
            </h2>

            <div className="space-y-6">
              {/* Template Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Default Document Request"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Tag Helper */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-2">
                  Available Tags
                </h3>
                <p className="text-xs text-blue-700 mb-3">
                  Click a tag to insert it at your cursor position in {activeField === 'subject' ? 'subject' : (activeField || 'template')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => (
                    <button
                      key={tag.key}
                      onClick={() => insertTag(tag.key)}
                      className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200"
                      title={`Insert ${tag.label} at cursor`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject *
                </label>
                <input
                  id="subject-input"
                  type="text"
                  value={formData.subjectTemplate}
                  onChange={(e) => handleInputChange('subject', e.target.value, e.target.selectionStart)}
                  onFocus={(e) => {
                    handleInputFocus('subject');
                    setCursorPosition(prev => ({ ...prev, subject: e.target.selectionStart || 0 }));
                  }}
                  onClick={(e) => {
                    const target = e.target as HTMLInputElement;
                    setCursorPosition(prev => ({ ...prev, subject: target.selectionStart || 0 }));
                  }}
                  onSelect={(e) => {
                    const target = e.target as HTMLInputElement;
                    setCursorPosition(prev => ({ ...prev, subject: target.selectionStart || 0 }));
                  }}
                  placeholder="e.g., Document Request - {{document_type}}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Body Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Body *
                </label>
                <textarea
                  id="body-input"
                  value={formData.bodyTemplate}
                  onChange={(e) => handleInputChange('body', e.target.value, e.target.selectionStart)}
                  onFocus={(e) => {
                    handleInputFocus('body');
                    setCursorPosition(prev => ({ ...prev, body: e.target.selectionStart || 0 }));
                  }}
                  onClick={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    setCursorPosition(prev => ({ ...prev, body: target.selectionStart || 0 }));
                  }}
                  onSelect={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    setCursorPosition(prev => ({ ...prev, body: target.selectionStart || 0 }));
                  }}
                  rows={10}
                  placeholder="e.g., Hello {{lead_name}},&#10;&#10;We need you to submit the following documents: {{document_type}}&#10;&#10;Please click the link below to upload:&#10;{{document_upload_link}}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Default Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
                  Set as default template
                </label>
              </div>

              {/* Email Send Behavior - Only for leads templates */}
              {templateType === 'leads' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Sending Behavior *
                  </label>
                  <select
                    value={formData.emailSendBehavior}
                    onChange={(e) => setFormData({ ...formData, emailSendBehavior: e.target.value as 'auto' | 'ask' | 'never' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="auto">Send email automatically</option>
                    <option value="ask">Ask before sending</option>
                    <option value="never">Don't send email</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.emailSendBehavior === 'auto' && 'Email will be sent automatically when a new lead is created'}
                    {formData.emailSendBehavior === 'ask' && 'User will be prompted to approve sending the email before it is sent'}
                    {formData.emailSendBehavior === 'never' && 'No email will be sent automatically'}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTemplate(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={editingTemplate ? handleUpdate : handleCreate}
                  disabled={!formData.title || !formData.subjectTemplate || !formData.bodyTemplate}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesContent;

